import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { useInvoiceRequests, useOrderHistory, getOrderItemsSnapshot, markInvoiceAsSent, InvoiceRequestOrder } from '../../lib/invoice-requests-hooks';
import { useBillingInfo, processPayment, openPrintableInvoice, PaymentRecord } from '../../lib/billing-hooks';
import { cn, formatCurrency } from '../../lib/utils';
import { FileText, Mail, Printer, CheckCircle2, AlertCircle, Loader2, Clock, History, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
type TabKey = 'pending' | 'sent' | 'history';
export function InvoiceRequestsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    pendingRequests,
    sentRequests,
    loading: requestsLoading,
    refetch: refetchRequests
  } = useInvoiceRequests(restaurant?.id);
  const {
    orders: historyOrders,
    loading: historyLoading,
    refetch: refetchHistory
  } = useOrderHistory(restaurant?.id, 200);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurant?.id);
  const [activeTab, setActiveTab] = useState<TabKey>('pending');
  const [processingKey, setProcessingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [invoiceModalOrder, setInvoiceModalOrder] = useState<InvoiceRequestOrder | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalLegalId, setModalLegalId] = useState('');
  const [modalEmail, setModalEmail] = useState('');
  // ─── Modal de impresión (NatuCafé) — pide nombre/cédula para el
  // comprobante físico antes de encolar el print job. ───────────────
  const [printModalOrder, setPrintModalOrder] = useState<InvoiceRequestOrder | null>(null);
  const [printName, setPrintName] = useState('');
  const [printLegalId, setPrintLegalId] = useState('');
  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({
      type,
      message
    });
    setTimeout(() => setToast(null), 3500);
  };
  const refetchAll = () => {
    refetchRequests();
    refetchHistory();
  };
  const openSendModal = (order: InvoiceRequestOrder) => {
    setInvoiceModalOrder(order);
    setModalName(order.invoice_customer_name?.trim() || order.customer_name?.trim() || '');
    setModalLegalId(order.invoice_legal_id?.trim() || '');
    setModalEmail(order.invoice_email?.trim() || '');
  };
  const closeSendModal = () => {
    setInvoiceModalOrder(null);
    setModalName('');
    setModalLegalId('');
    setModalEmail('');
  };
  // ─── Abrir / cerrar el modal de impresión (datos del cliente) ──────
  const openPrintModal = (order: InvoiceRequestOrder) => {
    setPrintModalOrder(order);
    setPrintName(order.invoice_customer_name?.trim() || order.customer_name?.trim() || '');
    setPrintLegalId(order.invoice_legal_id?.trim() || '');
  };
  const closePrintModal = () => {
    setPrintModalOrder(null);
    setPrintName('');
    setPrintLegalId('');
  };
  // ─── Asegura que la orden tenga un payment (lo crea si falta) ───────────
  // Reutiliza el payment vinculado a la orden si existe (caso "Enviar más
  // tarde" o factura ya enviada). Si no existe, crea uno nuevo con los
  // items de la orden, igual que el flujo de cobro en caja. Esto permite
  // tanto imprimir como enviar la factura desde el Historial aun cuando
  // la orden no haya pasado por el modal de cobro.
  const ensurePayment = async (order: InvoiceRequestOrder): Promise<PaymentRecord> => {
    if (!restaurant?.id) throw new Error('Restaurante no cargado.');
    const {
      supabase
    } = await import('../../lib/supabase');
    // 1) Si la orden ya tiene un payment vinculado, lo reutilizamos.
    if (order.invoice_payment_id) {
      const {
        data: existing,
        error
      } = await supabase.from('payments').select('*').eq('id', order.invoice_payment_id).single();
      if (!error && existing) return existing as PaymentRecord;
    }
    // 2) Si no, buscamos el último payment registrado para esa orden
    //    (cobros normales en caja generan payments sin marcar la orden).
    const {
      data: anyPayment
    } = await supabase.from('payments').select('*').eq('order_id', order.id).order('created_at', {
      ascending: false
    }).limit(1).maybeSingle();
    if (anyPayment) return anyPayment as PaymentRecord;
    // 3) Si nunca se cobró, generamos uno nuevo (mismo flujo que caja).
    const itemsSnapshot = await getOrderItemsSnapshot(order.id);
    if (itemsSnapshot.length === 0) {
      throw new Error('No se encontraron ítems para esta orden.');
    }
    const result = await processPayment({
      restaurantId: restaurant.id,
      orderId: order.id,
      tableId: null,
      amount: order.total,
      method: 'card',
      items: itemsSnapshot,
      isPartial: false,
      createdBy: tenantCtx?.userId || null
    });
    if (!result.success || !result.payment) {
      throw new Error(result.error || 'Error al crear el registro de pago.');
    }
    return result.payment;
  };
  // ─── Imprimir factura (acción independiente) ────────────────────────────
  // NatuCafé (restaurant_id = 19) → encola un order_print_jobs con
  // target_printer_type='invoice' para que el bridge dual lo envíe
  // DIRECTO a la impresora NOIZA_FACTURA configurada en
  // restaurant_printer_settings. Mismo patrón que usa PayTableModal
  // al cobrar, así el formato del ticket queda 100% consistente.
  //
  // Resto de restaurantes (Cabaña, Rancho, Capricho, etc.) →
  // fallback al flujo anterior (openPrintableInvoice abre una
  // ventana imprimible). Cero impacto en ellos.
  const handlePrint = async (order: InvoiceRequestOrder, customerName?: string, customerLegalId?: string) => {
    if (!restaurant?.id || !billingInfo) {
      showToast('error', 'Falta información de facturación del restaurante.');
      return;
    }
    const key = `${order.id}:print`;
    setProcessingKey(key);
    try {
      const payment = await ensurePayment(order);
      const isNatuCafe = String(restaurant.id) === '19';
      if (isNatuCafe) {
        const {
          supabase
        } = await import('../../lib/supabase');
        const {
          data: orderRow
        } = await supabase.from('orders').select('tracking_token, order_number, customer_name, customer_phone, delivery_type, table_id, notes, subtotal, delivery_fee, total, source, created_at').eq('id', order.id).single();
        const {
          data: itemRowsRaw
        } = await supabase.from('order_items').select('id, menu_item_id, item_name_snapshot, unit_price_snapshot, quantity, notes, station_code, line_total, status').eq('order_id', order.id);
        const itemRows = (itemRowsRaw || []).filter((r: any) => !['cancelled', 'canceled', 'removed', 'voided', 'deleted'].includes(String(r.status || '').toLowerCase().trim()));
        const itemIds = (itemRows || []).map((r: any) => r.id);
        const optionsByItemId: Record<string, any[]> = {};
        if (itemIds.length > 0) {
          const {
            data: optRows
          } = await supabase.from('order_item_options').select('order_item_id, option_group, option_name, price_delta').in('order_item_id', itemIds);
          (optRows || []).forEach((o: any) => {
            if (!optionsByItemId[o.order_item_id]) optionsByItemId[o.order_item_id] = [];
            optionsByItemId[o.order_item_id].push({
              option_group: o.option_group,
              option_name: o.option_name,
              price_delta: o.price_delta
            });
          });
        }
        const itemsPayload = (itemRows || []).map((r: any) => ({
          menu_item_id: r.menu_item_id,
          item_name_snapshot: r.item_name_snapshot,
          unit_price_snapshot: r.unit_price_snapshot,
          quantity: r.quantity,
          notes: r.notes,
          station_code: r.station_code,
          line_total: r.line_total,
          selected_options: optionsByItemId[r.id] || []
        }));
        // Datos del cliente para el comprobante impreso. Si el cajero
        // escribió nombre o cédula en el modal de impresión, el bridge
        // los imprime (show_customer_on_invoice = true). Si ambos van
        // vacíos, la factura sale en blanco como hoy. Mismos campos que
        // PayTableModal y que lee bridge_dual_printer_patch.py.
        const trimmedName = (customerName ?? '').trim();
        const trimmedLegalId = (customerLegalId ?? '').trim();
        const showCustomerOnInvoice = !!(trimmedName || trimmedLegalId);
        const invoicePayload = {
          trigger: `invoice_reprint_${payment.id}_${Date.now()}`,
          payment_id: payment.id,
          payment_method: payment.method,
          order_id: order.id,
          order_number: orderRow?.order_number || order.order_number,
          customer_name: trimmedName || orderRow?.customer_name || order.customer_name || '',
          customer_phone: orderRow?.customer_phone,
          delivery_type: orderRow?.delivery_type || order.delivery_type,
          table_id: orderRow?.table_id,
          notes: orderRow?.notes,
          subtotal: orderRow?.subtotal,
          delivery_fee: orderRow?.delivery_fee,
          total: orderRow?.total ?? order.total,
          source: orderRow?.source,
          created_at: orderRow?.created_at || order.created_at,
          items: itemsPayload,
          show_customer_on_invoice: showCustomerOnInvoice,
          invoice_customer_name: trimmedName || '',
          invoice_customer_id: trimmedLegalId || '',
          customer_id_number: trimmedLegalId || '',
          // ─── DEBUG TEMPORAL — identificar el origen del print job ──
          // Si el último invoice job en order_print_jobs NO trae estos
          // campos, fue creado por otro lugar (trigger / edge / código
          // viejo), no por esta página. Quitar tras el diagnóstico.
          debug_source: 'InvoiceRequestsPage',
          debug_version: 'invoice-customer-payload-v2',
          debug_created_at: new Date().toISOString()
        };
        // 🔎 DEBUG TEMPORAL (NatuCafé factura) — verificar EN DEVTOOLS que
        // los 4 campos del cliente viajan en el payload ANTES del insert.
        // Quitar este log una vez confirmado que llegan a order_print_jobs.
        const payloadFactura = invoicePayload;
        console.log('[NOIZA][NatuCafé][invoice-print] payloadFactura (InvoiceRequestsPage) →', {
          show_customer_on_invoice: payloadFactura.show_customer_on_invoice,
          invoice_customer_name: payloadFactura.invoice_customer_name,
          invoice_customer_id: payloadFactura.invoice_customer_id,
          customer_id_number: payloadFactura.customer_id_number,
          modal_customerName: trimmedName,
          modal_customerLegalId: trimmedLegalId
        }, payloadFactura);
        const {
          error: pjError
        } = await supabase.from('order_print_jobs').insert({
          restaurant_id: 19,
          order_id: orderRow?.tracking_token || null,
          order_number: orderRow?.order_number || order.order_number,
          status: 'pending',
          target_printer_type: 'invoice',
          payload: payloadFactura
        });
        if (pjError) {
          throw new Error(`No se pudo encolar la impresión: ${pjError.message}`);
        }
        showToast('success', '🖨 Enviado a la impresora NOIZA_FACTURA.');
        closePrintModal();
        refetchAll();
        return;
      }
      openPrintableInvoice({
        info: billingInfo,
        payment,
        tableLabel: `Orden #${order.order_number.split('-').pop()}`
      });
      showToast('success', 'Factura lista para imprimir.');
      refetchAll();
    } catch (err: any) {
      console.error('[NOIZA] Error printing invoice:', err);
      showToast('error', err.message || 'Error al imprimir la factura.');
    } finally {
      setProcessingKey(null);
    }
  };
  // ─── Click handler: abre el modal con los datos pre-cargados ───────────
  const handleSendInvoice = (order: InvoiceRequestOrder) => {
    if (!restaurant?.id || !billingInfo) {
      showToast('error', 'Falta información de facturación del restaurante.');
      return;
    }
    openSendModal(order);
  };
  // ─── Submit del modal: ejecuta exactamente el mismo flow que
  //     PayTableModal en "Sí, enviar ahora" → send-invoice-email ───────────
  const doSendInvoice = async (order: InvoiceRequestOrder, name: string, legalId: string, email: string) => {
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    const trimmedLegalId = legalId.trim();
    if (!trimmedEmail) {
      showToast('error', 'Falta el correo electrónico del cliente.');
      return;
    }
    const key = `${order.id}:send`;
    setProcessingKey(key);
    try {
      const {
        supabase
      } = await import('../../lib/supabase');
      const payment = await ensurePayment(order);
      // Persistir datos de facturación en la orden (idempotente).
      try {
        await supabase.from('orders').update({
          invoice_customer_name: trimmedName || null,
          invoice_email: trimmedEmail,
          invoice_legal_id: trimmedLegalId || null
        }).eq('id', order.id);
      } catch (e) {
        console.warn('[NOIZA] no se pudo persistir invoice_customer_name/email/legal_id:', e);
      }
      const {
        data: emailRes,
        error: emailErr
      } = await supabase.functions.invoke('send-invoice-email', {
        body: {
          payment_id: payment.id,
          customer_email: trimmedEmail,
          customer_name: trimmedName || null,
          customer_legal_id: trimmedLegalId || null
        }
      });
      if (emailErr) {
        let serverError = emailErr.message || 'Error invocando la función';
        try {
          const ctx: any = (emailErr as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            serverError = body?.error || body?.message || serverError;
          }
        } catch {
          /* swallow */
        }
        throw new Error(`No se pudo enviar el correo: ${serverError}`);
      }
      if (!emailRes?.success) {
        throw new Error(`Respuesta inesperada de la función: ${JSON.stringify(emailRes)}`);
      }
      const markResult = await markInvoiceAsSent(order.id, payment.id);
      if (!markResult.success) {
        console.warn('[NOIZA] markInvoiceAsSent falló pero el correo ya salió:', markResult.error);
      }
      showToast('success', `Factura enviada a ${trimmedEmail}.`);
      closeSendModal();
      refetchAll();
    } catch (err: any) {
      console.error('[NOIZA] Error sending invoice:', err);
      showToast('error', err.message || 'Error inesperado al enviar la factura.');
    } finally {
      setProcessingKey(null);
    }
  };
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5009" data-mp="2jtkb41jgp6af">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-5010" data-mp="2ew28esw1fnav">
          <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-5011" data-mp="3v64lp893k39h" />
        </div>
      </AdminLayout>;
  }
  const displayRequests: InvoiceRequestOrder[] = activeTab === 'pending' ? pendingRequests : activeTab === 'sent' ? sentRequests : historyOrders;
  const isListLoading = activeTab === 'history' ? historyLoading : requestsLoading;
  const modalSending = invoiceModalOrder !== null && processingKey === `${invoiceModalOrder.id}:send`;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5012" data-mp="g36427neohl9">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-5013" data-mp="3ejqgv1x2l02r">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-5014" data-mp="3jibmjgv3cbyl">
          <div data-id="element-5015" data-mp="3tahckl1s8jb5">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-5016" data-mp="3tazh04ylm3u5">
              <FileText className="w-8 h-8 text-amber-500" data-id="element-5017" data-mp="2vvv7du8hq8fg" />
              Facturas
              {pendingRequests.length > 0 && <span className="bg-amber-500/20 text-amber-500 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-5018" data-mp="30sbzezit96xt">
                  {pendingRequests.length} pendientes
                </span>}
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-5019" data-mp="18ydo7o60mywl">
              Imprimí o enviá por correo la factura de cualquier pedido del
              historial.
            </p>
          </div>
        </div>

        {/* Billing Info Warning */}
        {!infoLoading && !billingInfo && <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 flex gap-3" data-id="element-5020" data-mp="z2anmmqtb8br">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-5021" data-mp="u7d58p7n7mm7" />
            <div data-id="element-5022" data-mp="q8e136tqjtvt">
              <h3 className="text-sm font-bold text-red-400" data-id="element-5023" data-mp="q5rxfi9ytyiv">
                Falta información legal
              </h3>
              <p className="text-xs text-red-400/70 mt-1" data-id="element-5024" data-mp="g9osy5rn5nsr">
                No se han configurado los datos legales de este restaurante. No
                podrás generar facturas hasta que se configuren.
              </p>
            </div>
          </div>}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-px" data-id="element-5025" data-mp="202yfl1qeo0wx">
          <button onClick={() => setActiveTab('pending')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors', activeTab === 'pending' ? 'border-amber-500 text-amber-500' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-5026" data-mp="245l9d86mzvwr">
            Pendientes ({pendingRequests.length})
          </button>
          <button onClick={() => setActiveTab('sent')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors', activeTab === 'sent' ? 'border-emerald-500 text-emerald-500' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-5027" data-mp="17gdnu060pmsu">
            Enviadas hoy ({sentRequests.length})
          </button>
          <button onClick={() => setActiveTab('history')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5', activeTab === 'history' ? 'border-sky-400 text-sky-400' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-5028" data-mp="iwab07l530yd">
            <History className="w-4 h-4" data-id="element-5029" data-mp="11tbh5vqfj73w" />
            Historial ({historyOrders.length})
          </button>
        </div>

        {/* Helper text per tab */}
        {activeTab === 'history' && <p className="text-xs text-white/40 mb-4" data-id="element-5030" data-mp="1bkfyh23kv41w">
            Todos los pedidos no cancelados. Podés imprimir la factura, enviarla
            por correo, o ambas — independientemente de si el cliente la
            solicitó.
          </p>}

        {/* List */}
        {isListLoading ? <div className="flex justify-center py-20" data-id="element-5031" data-mp="1gjshtcv8pszq">
            <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-5032" data-mp="1ggwmlwas1iez" />
          </div> : displayRequests.length === 0 ? <div className="py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-5033" data-mp="1o35xh3slo4ie">
            {activeTab === 'pending' ? <>
                <CheckCircle2 className="w-16 h-16 text-emerald-500/20 mb-4" data-id="element-5034" data-mp="1y0kf36q9ezca" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-5035" data-mp="1aazsmduwdjy9">
                  🎉 Sin facturas pendientes
                </h3>
                <p className="text-sm" data-id="element-5036" data-mp="15dg32hsr1kr0">Todo está al día.</p>
              </> : activeTab === 'sent' ? <>
                <FileText className="w-16 h-16 text-white/10 mb-4" data-id="element-5037" data-mp="1j5e6qd3oyofs" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-5038" data-mp="1eb9ylqiowad4">
                  No hay facturas enviadas hoy
                </h3>
              </> : <>
                <History className="w-16 h-16 text-white/10 mb-4" data-id="element-5039" data-mp="mkvpe7lmfm7s" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-5040" data-mp="ch6wy1rl4wj6">
                  No hay pedidos en el historial
                </h3>
              </>}
          </div> : <div className="grid gap-4" data-id="element-5041" data-mp="2k2iyp7w87dg">
            {displayRequests.map(order => {
          const printing = processingKey === `${order.id}:print`;
          const sending = processingKey === `${order.id}:send`;
          const busy = printing || sending;
          const shortOrderNum = order.order_number.split('-').pop();
          const timeAgo = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
          const alreadySent = !!order.invoice_sent_at;
          const hasBillingData = !!order.invoice_customer_name || !!order.invoice_email || !!order.invoice_legal_id;
          return <div key={order.id} className="bg-[#141414] border border-white/5 rounded-2xl p-4 sm:p-5 flex flex-col md:flex-row gap-4 md:items-center justify-between transition-all hover:border-white/10" data-id="element-5042" data-mp="3ax9heahpjky1">
                  <div className="flex-1 min-w-0" data-id="element-5043" data-mp="17pscsn41ap6u">
                    <div className="flex items-center gap-3 mb-2 flex-wrap" data-id="element-5044" data-mp="17oh9spiexlgs">
                      <span className="text-lg font-black text-white tabular-nums" data-id="element-5045" data-mp="12rh3qelkcipo">
                        #{shortOrderNum}
                      </span>
                      <span className="text-sm font-bold text-[#F4D35E]" data-id="element-5046" data-mp="tqt08dtsq0r1">
                        {formatCurrency(order.total)}
                      </span>
                      <span className="text-xs font-medium text-white/40 flex items-center gap-1" data-id="element-5047" data-mp="ou2bz37u7yuj">
                        <Clock className="w-3 h-3" data-id="element-5048" data-mp="juzbzx6psitf" />
                        {timeAgo < 60 ? `Hace ${timeAgo}m` : timeAgo < 60 * 24 ? `Hace ${Math.floor(timeAgo / 60)}h` : `Hace ${Math.floor(timeAgo / (60 * 24))}d`}
                      </span>
                      {alreadySent && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-md text-[10px] font-bold uppercase tracking-wider" data-id="element-5049" data-mp="2qnf4l5oiyb4j">
                          <CheckCircle2 className="w-3 h-3" data-id="element-5050" data-mp="30erzk65mdhes" /> Enviada
                        </span>}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm" data-id="element-5051" data-mp="27qdy1gfbo2yh">
                      <div data-id="element-5052" data-mp="27sgwmoujj4et">
                        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-0.5" data-id="element-5053" data-mp="2cp7bj8ulztyn">
                          Datos de Facturación
                        </p>
                        {hasBillingData ? <>
                            <p className="font-bold text-white truncate" data-id="element-5054" data-mp="3o702ei5rhrzq">
                              {order.invoice_customer_name || order.customer_name}
                            </p>
                            {order.invoice_legal_id && <p className="text-white/60 text-xs mt-0.5" data-id="element-5055" data-mp="3mpb7rt3txj67">
                                ID: {order.invoice_legal_id}
                              </p>}
                            {order.invoice_email && <p className="text-white/60 text-xs mt-0.5 flex items-center gap-1 truncate" data-id="element-5056" data-mp="dia4gq5f83u4">
                                <Mail className="w-3 h-3 shrink-0" data-id="element-5057" data-mp="9i017l90jriy" />{' '}
                                {order.invoice_email}
                              </p>}
                          </> : <p className="text-white/40 text-xs italic" data-id="element-5058" data-mp="iblet19fz2qs">
                            Sin datos — se pedirán al enviar.
                          </p>}
                      </div>

                      <div data-id="element-5059" data-mp="s5vaartokh5q">
                        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-0.5" data-id="element-5060" data-mp="1bsycdw6rlc2r">
                          Datos de la Orden
                        </p>
                        <p className="text-white/80 truncate" data-id="element-5061" data-mp="11vait9kwaam6">
                          {order.customer_name}
                        </p>
                        <p className="text-white/60 text-xs mt-0.5 capitalize" data-id="element-5062" data-mp="16tc4815smm0t">
                          {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para llevar' : 'Local'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row md:flex-col gap-2 shrink-0 border-t border-white/5 pt-4 md:border-t-0 md:pt-0 md:min-w-[200px]" data-id="element-5063" data-mp="o1t1lvfol9zb">
                    <Button onClick={() => {
                // NatuCafé: pedir nombre/cédula en un modal antes
                // de imprimir. Otros restaurantes: flujo directo.
                if (String(restaurant.id) === '19') {
                  openPrintModal(order);
                } else {
                  handlePrint(order);
                }
              }} disabled={busy || !billingInfo} variant="outline" className="flex-1 md:flex-none border-white/10 text-white hover:bg-white/5 font-bold" data-id="element-5064" data-mp="e5zexj89003t">
                      {printing ? <span className="flex items-center justify-center gap-2" data-id="element-5065" data-mp="40hoemhnzyit">
                          <Loader2 className="w-4 h-4 animate-spin" data-id="element-5066" data-mp="3v8vgn5zkcygg" />
                          Imprimiendo...
                        </span> : <span className="flex items-center justify-center gap-2" data-id="element-5067" data-mp="2jsx5mwkts2cl">
                          <Printer className="w-4 h-4" data-id="element-5068" data-mp="2jv1ezu1httk9" /> Imprimir
                        </span>}
                    </Button>
                    <Button onClick={() => handleSendInvoice(order)} disabled={busy || !billingInfo} className="flex-1 md:flex-none bg-amber-500 hover:bg-amber-400 text-black font-bold" data-id="element-5069" data-mp="1497pfb5yva31">
                      {sending ? <span className="flex items-center justify-center gap-2" data-id="element-5070" data-mp="1iy3s0sp97uw9">
                          <Loader2 className="w-4 h-4 animate-spin" data-id="element-5071" data-mp="1mzpb4vsdt5ew" />
                          Enviando...
                        </span> : <span className="flex items-center justify-center gap-2" data-id="element-5072" data-mp="1wymxbs2c4muq">
                          <Mail className="w-4 h-4" data-id="element-5073" data-mp="1wwk83a90axr2" />{' '}
                          {alreadySent ? 'Reenviar correo' : 'Enviar correo'}
                        </span>}
                    </Button>
                  </div>
                </div>;
        })}
          </div>}

        {/* Toast */}
        {toast && <div className="fixed bottom-6 right-6 z-50 pointer-events-none" data-id="element-5074" data-mp="1zi1ijpigaa2f">
            <div className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-5075" data-mp="2e8ih913palvm">
              <span className="flex-1" data-id="element-5076" data-mp="1vhrb08f0fblg">{toast.message}</span>
            </div>
          </div>}

        {/* ─── Modal: Enviar factura por correo ────────────────────────── */}
        {/* Replica el mismo formulario que PayTableModal abre cuando el  */}
        {/* cliente solicita factura. Mismo flujo: nombre / cédula /      */}
        {/* correo → invoke send-invoice-email → markInvoiceAsSent.       */}
        {invoiceModalOrder && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-5077" data-mp="39j5kx5tx4q8a">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" data-id="element-5078" data-mp="30ge56tu0f8au">
              {/* Header */}
              <div className="flex items-start justify-between p-5 border-b border-gray-100" data-id="element-5079" data-mp="34iraxi8rb67m">
                <div className="flex items-start gap-3" data-id="element-5080" data-mp="3s8vclk2dwvvz">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0" data-id="element-5081" data-mp="119b9y3515ia">
                    <FileText className="w-5 h-5 text-amber-600" data-id="element-5082" data-mp="3jfs2nlqy7163" />
                  </div>
                  <div data-id="element-5083" data-mp="3nglsiknyaq1m">
                    <h2 className="text-lg font-black text-gray-900" data-id="element-5084" data-mp="3nfsgb46838c3">
                      Enviar factura por correo
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-5085" data-mp="eyxp4lysfcdu">
                      Pedido #{invoiceModalOrder.order_number.split('-').pop()}{' '}
                      · {formatCurrency(invoiceModalOrder.total)} · Régimen
                      Simplificado
                    </p>
                  </div>
                </div>
                <button onClick={closeSendModal} disabled={modalSending} className="text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Cerrar" data-id="element-5086" data-mp="1u1cg3xgn931z">
                  <X className="w-5 h-5" data-id="element-5087" data-mp="16b8bpnvmsz9x" />
                </button>
              </div>

              {/* Form */}
              <div className="p-5 overflow-y-auto space-y-5" data-id="element-5088" data-mp="1ba1plhetysam">
                <div data-id="element-5089" data-mp="1bcxop92ezn0n">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-5090" data-mp="1g9edm5hfu5es">
                    A nombre de
                  </label>
                  <input type="text" value={modalName} onChange={e => setModalName(e.target.value)} placeholder="Nombre completo del cliente" disabled={modalSending} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" autoFocus data-id="element-5091" data-mp="2mrug2pm2mob6" />
                </div>

                <div data-id="element-5092" data-mp="3th5jqg7sak5b">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-5093" data-mp="3thp4e4otjmn1">
                    Cédula / Identificación{' '}
                    <span className="text-gray-400 normal-case font-medium" data-id="element-5094" data-mp="3jhepmfn4y75r">
                      (opcional)
                    </span>
                  </label>
                  <input type="text" value={modalLegalId} onChange={e => setModalLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" disabled={modalSending} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" data-id="element-5095" data-mp="3elhgataai12h" />
                </div>

                <div data-id="element-5096" data-mp="32841wmc2w4ez">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-5097" data-mp="329nkijl7irjx">
                    Correo electrónico
                  </label>
                  <input type="email" value={modalEmail} onChange={e => setModalEmail(e.target.value)} placeholder="cliente@correo.com" disabled={modalSending} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" data-id="element-5098" data-mp="3uxt4330iyai6" />
                  <p className="text-xs text-gray-500 mt-1.5" data-id="element-5099" data-mp="21zntzu44bg4o">
                    Se enviará la factura electrónica a este correo de forma
                    automática.
                  </p>
                </div>
              </div>

              {/* Footer actions */}
              <div className="p-5 border-t border-gray-100 flex gap-3" data-id="element-5100" data-mp="2701tgnrnlhr3">
                <Button onClick={closeSendModal} disabled={modalSending} variant="outline" className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50 font-bold" data-id="element-5101" data-mp="26xpfsmsxhtju">
                  Cancelar
                </Button>
                <Button onClick={() => doSendInvoice(invoiceModalOrder, modalName, modalLegalId, modalEmail)} disabled={modalSending || !modalEmail.trim()} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-bold" data-id="element-5102" data-mp="1t1twwgyj4igg">
                  {modalSending ? <span className="flex items-center justify-center gap-2" data-id="element-5103" data-mp="2sqxred6mql9">
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-5104" data-mp="7sdaznt0g6u2" />
                      Enviando...
                    </span> : <span className="flex items-center justify-center gap-2" data-id="element-5105" data-mp="hjzppu4k4o1k">
                      <Mail className="w-4 h-4" data-id="element-5106" data-mp="hm2de7i8m534" />
                      Enviar factura
                    </span>}
                </Button>
              </div>
            </div>
          </div>}

        {/* ─── Modal: Imprimir comprobante con datos del cliente ───────── */}
        {printModalOrder && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-5107" data-mp="2scanq6ffdj1f">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" data-id="element-5108" data-mp="2xbx0yfv96za8">
              {/* Header */}
              <div className="flex items-start justify-between p-5 border-b border-gray-100" data-id="element-5109" data-mp="2pt612kyf7bb1">
                <div className="flex items-start gap-3" data-id="element-5110" data-mp="2kytcxxm01bsy">
                  <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center shrink-0" data-id="element-5111" data-mp="2g2baj4e55pjs">
                    <Printer className="w-5 h-5 text-cyan-600" data-id="element-5112" data-mp="2b41hazbz8z0t" />
                  </div>
                  <div data-id="element-5113" data-mp="38ow3g9a9m9pg">
                    <h2 className="text-lg font-black text-gray-900" data-id="element-5114" data-mp="38o4hlq6gzqcd">
                      Imprimir comprobante
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-5115" data-mp="2yrrgi78lia7l">
                      Pedido #{printModalOrder.order_number.split('-').pop()} ·{' '}
                      {formatCurrency(printModalOrder.total)}
                    </p>
                  </div>
                </div>
                <button onClick={closePrintModal} disabled={processingKey === `${printModalOrder.id}:print`} className="text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Cerrar" data-id="element-5116" data-mp="1jbafm9biyi46">
                  <X className="w-5 h-5" data-id="element-5117" data-mp="1av1pikut3c9u" />
                </button>
              </div>

              {/* Form */}
              <div className="p-5 overflow-y-auto space-y-5" data-id="element-5118" data-mp="1fsbk0ex1ye4j">
                <p className="text-xs text-gray-500 -mt-1" data-id="element-5119" data-mp="12287wgrw00st">
                  Estos datos saldrán en el comprobante físico. Si los dejás en
                  blanco, la factura se imprime sin datos del cliente.
                </p>
                <div data-id="element-5120" data-mp="s4tp4aihqzxc">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-5121" data-mp="s5bsrs7hggfa">
                    Nombre del cliente
                  </label>
                  <input type="text" value={printName} onChange={e => setPrintName(e.target.value)} placeholder="Nombre completo del cliente" disabled={processingKey === `${printModalOrder.id}:print`} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" autoFocus data-id="element-5122" data-mp="j45bhgx0f1kz" />
                </div>
                <div data-id="element-5123" data-mp="ne7w1qq4j7jp">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-5124" data-mp="ndevj7hrdea0">
                    Cédula / Identificación{' '}
                    <span className="text-gray-400 normal-case font-medium" data-id="element-5125" data-mp="w7t8h3etgcpy">
                      (opcional)
                    </span>
                  </label>
                  <input type="text" value={printLegalId} onChange={e => setPrintLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" disabled={processingKey === `${printModalOrder.id}:print`} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" data-id="element-5126" data-mp="s8c287rkfbjq" />
                </div>
              </div>

              {/* Footer actions */}
              <div className="p-5 border-t border-gray-100 flex gap-3" data-id="element-5127" data-mp="1ove53or46xbo">
                <Button onClick={closePrintModal} disabled={processingKey === `${printModalOrder.id}:print`} variant="outline" className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50 font-bold" data-id="element-5128" data-mp="1ot9qabrogdwm">
                  Cancelar
                </Button>
                <Button onClick={() => handlePrint(printModalOrder, printName, printLegalId)} disabled={processingKey === `${printModalOrder.id}:print`} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-bold" data-id="element-5129" data-mp="165eir9mnrwby">
                  {processingKey === `${printModalOrder.id}:print` ? <span className="flex items-center justify-center gap-2" data-id="element-5130" data-mp="tkzi7n054ifh">
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-5131" data-mp="onfr3micgb64" />
                      Imprimiendo...
                    </span> : <span className="flex items-center justify-center gap-2" data-id="element-5132" data-mp="knfd22z5q1zl">
                      <Printer className="w-4 h-4" data-id="element-5133" data-mp="ft1ddodgp8p4" />
                      Imprimir comprobante
                    </span>}
                </Button>
              </div>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}